import React from "react";
import { connect } from "@formily/react";
import { Tooltip, Popconfirm } from "antd";
import { useAuth } from "react-oidc-context";
import { getConfigValue } from "@rumble-it/common-lib/src/config";
import { twMerge } from "tailwind-merge";

/** ----------------------------------------------------------------------------- *
 *  Dealer Intake Header Component
 * ----------------------------------------------------------------------------- */

interface LogoProps {
  path: string;
  alt?: string;
  title?: string;
}

interface SwitcherOption {
  label: string;
  value: string;
}

interface SwitcherProps {
  options?: SwitcherOption[];
}

export interface DealerIntakeHeaderProps {
  title: string; // Title text next to the logo
  alt?: string; // Alt text for the logo
  logo?: string; // Path to the logo
  className?: string; // Additional class names for the header
  style?: React.CSSProperties; // Inline styles for the header
}

/* -----------------------------------------------------------------------------
 *  Logo
 * --------------------------------------------------------------------------- */
const Logo: React.FC<LogoProps> = ({ path, alt, title }) => (
  <div className="flex">
    <h1 className="text-2xl flex items-center gap-2">
      {title && <span className="font-bold">{title}</span>}
      <div className="text-neutral-700 ml-2 pl-4 border-l-2 border-black h-auto w-48 my-2">
        <img src={path} alt={alt ?? title ?? "logo"} className="py-4 h-16" />
      </div>
    </h1>
  </div>
);

/* -----------------------------------------------------------------------------
 *  Switcher (module switch + logout)
 *  Vervangen: IconWithPopup -> Antd Tooltip
 * --------------------------------------------------------------------------- */
const Switcher: React.FC<SwitcherProps> = () => {
  const auth = useAuth();
  const cdnUrl = getConfigValue("cdnUrl");

  const handleLogout = async () => {
    await auth.signoutRedirect();
  };

  return (
    <div className="flex flex-row items-center gap-2">
      {/* Switch module */}
      <Tooltip placement="top" title="Switch van module" mouseEnterDelay={0.1}>
        <a href="/" aria-label="Switch van module" className="inline-flex">
          <img
            src={`${cdnUrl}/static/dealer-intake/switch.svg`}
            alt="Wisselen"
            className="w-10 h-auto"
          />
        </a>
      </Tooltip>

      {/* Logout */}
      <Popconfirm
        title="Weet je zeker dat je wilt uitloggen?"
        okText="Ja"
        cancelText="Nee"
        onConfirm={handleLogout}
        placement="bottomRight"
      >
        <Tooltip placement="top" title="Uitloggen" mouseEnterDelay={0.1}>
          <button type="button" aria-label="Uitloggen" className="inline-flex">
            <img
              src={`${cdnUrl}/static/dealer-intake/sign-out.svg`}
              alt="Uitloggen"
              className="w-10 h-auto"
            />
          </button>
        </Tooltip>
      </Popconfirm>
    </div>
  );
};

/* -----------------------------------------------------------------------------
 *  DealerIntakeHeader (export)
 * --------------------------------------------------------------------------- */
export const DealerIntakeHeader: React.FC<DealerIntakeHeaderProps> = ({
  title = "Dealer Intake",
  alt = "Rumble",
  logo = `${getConfigValue("cdnUrl")}/static/assets/rumble/logo.svg`,
  className,
  style,
}) => (
  <header
    className={twMerge("flex justify-between items-center w-full", className)}
    style={style}
  >
    <Logo path={logo} alt={alt} title={title} />
    <Switcher />
  </header>
);

/* -----------------------------------------------------------------------------
 *  Formily-connected variant
 * --------------------------------------------------------------------------- */
export const RumbleDealerIntakeHeader = connect(DealerIntakeHeader);
export default RumbleDealerIntakeHeader;
